import Pusher from 'pusher-js'

import filter from 'lodash/filter'

import client from '@/utils/client'
import urls from '@/utils/urls'

import { ARTIST_NOTIFICATIONS } from '@/constants'

import * as mutationTypes from '../mutationTypes'

var defaultState = {
  socket: null,
  notificationChannel: null,
  notificationId: null,
  liveChatChannel: null,
  liveChatArtistChannel: null,
  liveChatId: null,
  liveChatArtistId: null,
  notifications: [],
  chatMessages: [],
  chatNotification: null,

  latestMessage: null
}

export const mutations = {
  setConfig (state, { apiKey, cluster }) {
    if (state.socket) return
    state.socket = new Pusher(apiKey, { cluster })
  },

  setLiveChatChannel (state, { channelId, channel }) {
    state.liveChatId = channelId
    state.liveChatChannel = channel
    state.chatMessages = []
  },
  setArtistChatChannel (state, { channelId, channel }) {
    state.liveChatArtistId = channelId
    state.liveChatArtistChannel = channel
    state.chatMessages = []
  },
  clearLiveChatChannel (state) {
    state.liveChatId = null
    state.liveChatChannel = null
    state.liveChatArtistId = null
    state.liveChatArtistChannel = null
    state.chatMessages = []
  },
  addChatMessage (state, message) {
    if (state.chatMessages.length) {
      const prev = state.chatMessages[state.chatMessages.length - 1]
      const delta = Math.abs(message.time - prev.time)
      if (
        delta < 5 &&
        prev.channel === message.channel &&
        prev.action === message.action &&
        prev.message === message.message &&
        (!prev.sender || !message.sender || prev.sender.id === message.sender.id)
      ) return
    }
    state.chatMessages.push(message)
  },
  clearChatMessages (state) {
    state.chatMessages = []
  },
  deleteChatMessage (state, message) {
    let idx = state.chatMessages.indexOf(message)
    if (idx !== -1) state.chatMessages.splice(idx, 1)
  },
  setChatNotification (state, payload) {
    state.chatNotification = payload
  },

  setNotificationChannel (state, { channelId, channel, initial }) {
    state.notificationId = channelId
    state.notificationChannel = channel
    state.notifications = initial
  },
  clearNotificationChannel (state) {
    state.notificationId = null
    state.notificationChannel = null
    state.notifications = []
  },
  addNotification (state, notification) {
    state.notifications.unshift(notification)
  },
  clearArtistNotifications (state) {
    state.notifications = filter(state.notifications, n => ARTIST_NOTIFICATIONS.indexOf(n.type) === -1)
  },
  clearFanNotifications (state) {
    state.notifications = filter(state.notifications, n => ARTIST_NOTIFICATIONS.indexOf(n.type) !== -1)
  },
  setLatestMessage (state, message) {
    state.latestMessage = message
  }
}

export const getters = {
  notifications: state => state.notifications,
  artistNotifications: state => filter(state.notifications, n => ARTIST_NOTIFICATIONS.indexOf(n.type) !== -1),
  fanNotifications: state => filter(state.notifications, n => ARTIST_NOTIFICATIONS.indexOf(n.type) === -1),
  chatMessages: state => state.chatMessages,
  chatNotify: state => state.chatNotification,
  latestMessage: state => state.latestMessage
}

export const actions = {
  startLiveChat ({ commit, state, dispatch }, { channelId, artistChannelId = null }) {
    if (!state.socket) return
    if (!channelId) throw new Error('live chat channel is not defined')

    if (state.liveChatId === channelId && state.artistChannelId === artistChannelId) return

    if (state.liveChatChannel || state.liveChatArtistChannel) dispatch('stopLiveChat')

    const channel = state.socket.subscribe(channelId)
    channel.bind('message', data => {
      data.channel = 'chat'
      commit('addChatMessage', data)
    })
    channel.bind('notification', data => {
      commit('setChatNotification', data)
    })
    commit('setLiveChatChannel', { channelId, channel })

    if (artistChannelId) {
      const artistChannel = state.socket.subscribe(artistChannelId)
      artistChannel.bind('message', data => {
        data.channel = 'artist-chat'
        commit('addChatMessage', data)
      })
      artistChannel.bind('notification', data => {
        commit('setChatNotification', data)
      })
      commit('setArtistChatChannel', { artistChannelId, artistChannel })
    }
  },
  stopLiveChat ({ commit, state }) {
    if (!state.socket) return

    if (state.liveChatChannel) {
      state.liveChatChannel.unbind_all()
      state.socket.unsubscribe(state.liveChatId)
    }

    if (state.liveChatArtistChannel) {
      state.liveChatArtistChannel.unbind_all()
      state.socket.unsubscribe(state.liveChatArtistId)
    }

    commit('clearLiveChatChannel')
  },
  injectOwnMessage ({ commit, rootGetters }, { message, action = 'message' }) {
    commit('addChatMessage', {
      sender: rootGetters['profile/currentUser'],
      time: new Date().toISOString(),
      message: message,
      action: action
    })
  },
  deleteMessage ({ commit }, message) {
    commit('deleteChatMessage', message)
  },
  async initNotifications ({ commit, state, dispatch }) {
    if (state.notificationChannel) return

    const processNotification = data => {
      if (data.type === 'new-message') {
        const { inbox, message } = data.content
        const mutation = inbox.is_artist_inbox ? mutationTypes.SET_ARTIST_INBOX : mutationTypes.SET_FAN_INBOX

        commit('profile/' + mutation, inbox, {root: true})
        message.inbox = inbox
        commit('setLatestMessage', message)
      } else {
        commit('addNotification', data)
      }
    }

    const response = await client.get(urls.profile.notifications())
    const config = response.data.config

    if (!state.socket) {
      commit('setConfig', { apiKey: config.api_key, cluster: config.cluster })
    }

    const channel = state.socket.subscribe(config.channel)
    channel.bind('notification', processNotification)

    commit('setNotificationChannel', {
      channelId: config.channel,
      channel,
      initial: response.data.notifications
    })
  },
  readNotifications ({ commit }, { artistMode }) {
    commit(artistMode ? 'clearArtistNotifications' : 'clearFanNotifications')
    client.post(urls.profile.notifications(), {artist: artistMode})
  },
  stopNotifications ({ state, commit }) {
    if (!state.socket) return

    if (state.notificationChannel) {
      state.notificationChannel.unbind_all()
      state.socket.unsubscribe(state.notificationId)
    }

    commit('clearNotificationChannel')
  }
}

export default {
  namespaced: true,
  state: defaultState,
  mutations: mutations,
  getters: getters,
  actions: actions
}



// WEBPACK FOOTER //
// ./src/store/modules/realtime.js